// frontend/modules/in-app-notifications/views/InAppNotificationDetailView.tsx
import React, { useEffect } from 'react';
import { Card, Typography, Space, Button, Tag, Divider, Alert, Spin } from 'antd';
import { 
    ArrowLeftOutlined, 
    EyeOutlined, 
    CheckOutlined, 
    DeleteOutlined, 
    LinkOutlined,
    ClockCircleOutlined,
    ExclamationCircleOutlined,
    InfoCircleOutlined,
    WarningOutlined,
    AlertOutlined,
    ArrowRightOutlined
} from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import { useInAppNotifications } from '../hooks/useInAppNotifications';
import { useTheme } from '@/components/common/ThemeProvider';
import { formatDistanceToNow, format } from 'date-fns';
import { NotificationPriority, ActionButton } from '../types';

const { Title, Text, Paragraph } = Typography;

export const InAppNotificationDetailView: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { dark } = useTheme();
    
    const {
        selectedNotification,
        setSelectedNotification,
        markAsRead,
        markAsAttended,
        deleteNotification,
        isMarkingAsRead,
        isMarkingAsAttended,
        isDeleting
    } = useInAppNotifications();

    // Mock query for single notification - replace with actual Orval hook
    const notificationId = id ? parseInt(id) : null;
    const isLoading = false; // This will come from the actual query

    useEffect(() => {
        if (notificationId) {
            // This will be replaced with actual API call
            // For now, we'll use mock data or find from existing notifications
            setSelectedNotification(null);
        }
    }, [notificationId, setSelectedNotification]);

    const handleBack = () => {
        navigate('/notifications');
    };

    const handleMarkAsRead = async () => {
        if (selectedNotification && selectedNotification.id && !selectedNotification.read) {
            try {
                await markAsRead(selectedNotification.id);
            } catch (error) {
                console.error('Error marking as read:', error);
            }
        }
    };

    const handleMarkAsAttended = async () => {
        if (selectedNotification && selectedNotification.id && !selectedNotification.attended) {
            try {
                await markAsAttended(selectedNotification.id);
            } catch (error) {
                console.error('Error marking as attended:', error);
            }
        }
    };

    const handleDelete = async () => {
        if (selectedNotification && selectedNotification.id) {
            try {
                await deleteNotification(selectedNotification.id);
                navigate('/notifications');
            } catch (error) {
                console.error('Error deleting notification:', error);
            }
        }
    };

    const handleActionLink = () => {
        const url = content.primaryAction?.url || content.actionLink || selectedNotification?.actionLink;
        if (url) {
            if (content.primaryAction?.external) {
                window.open(url, '_blank');
            } else {
                window.location.href = url;
            }
        }
    };

    const getCategoryColor = (category?: string) => {
        switch (category?.toUpperCase()) {
            case 'LOAN':
                return 'blue';
            case 'PAYMENT':
                return 'green';
            case 'SYSTEM':
                return 'purple';
            case 'SECURITY':
                return 'red';
            case 'REMINDER':
                return 'orange';
            default:
                return 'default';
        }
    };

    const getPriorityConfig = (priority?: string) => {
        const validPriority = (priority as NotificationPriority) || 'INFORMATIONAL';
        switch (validPriority) {
            case 'CRITICAL':
                return {
                    color: '#ff4d4f',
                    icon: <AlertOutlined />,
                    label: 'Critical',
                    description: 'Urgent action required'
                };
            case 'ALERT':
                return {
                    color: '#fa8c16',
                    icon: <ExclamationCircleOutlined />,
                    label: 'Alert',
                    description: 'Action recommended'
                };
            case 'WARNING':
                return {
                    color: '#faad14',
                    icon: <WarningOutlined />,
                    label: 'Warning',
                    description: 'May need attention'
                };
            case 'INFORMATIONAL':
            default:
                return {
                    color: '#1890ff',
                    icon: <InfoCircleOutlined />,
                    label: 'Info',
                    description: 'For your information'
                };
        }
    };

    const content = selectedNotification?.renderedContent || {
        title: selectedNotification?.title || '',
        message: selectedNotification?.message || '',
        priority: selectedNotification?.priority || 'INFORMATIONAL',
        category: selectedNotification?.category || '',
        color: getPriorityConfig(selectedNotification?.priority || 'INFORMATIONAL').color
    };

    const priorityConfig = getPriorityConfig(content.priority);

    const formatDate = (dateString?: string) => {
        if (!dateString) {
            return '';
        }
        try {
            return format(new Date(dateString), 'PPpp');
        } catch {
            return dateString;
        }
    };

    const formatRelativeDate = (dateString?: string) => {
        if (!dateString) {
            return '';
        }
        try {
            return formatDistanceToNow(new Date(dateString), { addSuffix: true });
        } catch {
            return dateString;
        }
    };

    if (isLoading) {
        return (
            <div className={`min-h-screen p-6 ${dark ? 'bg-gray-900 text-gray-100' : 'bg-gray-100 text-gray-800'}`}>
                <div className="container mx-auto max-w-4xl">
                    <div className="flex justify-center items-center py-8">
                        <Spin size="large" />
                    </div>
                </div>
            </div>
        );
    }

    if (!selectedNotification) {
        return (
            <div className={`min-h-screen p-6 ${dark ? 'bg-gray-900 text-gray-100' : 'bg-gray-100 text-gray-800'}`}>
                <div className="container mx-auto max-w-4xl">
                    <Button 
                        icon={<ArrowLeftOutlined />} 
                        onClick={handleBack}
                        className="mb-4"
                    >
                        Back to Notifications
                    </Button>
                    <Alert
                        message="Notification Not Found"
                        description="The requested notification could not be found."
                        type="warning"
                        showIcon
                    />
                </div>
            </div>
        );
    }

    return (
        <div className={`min-h-screen p-6 ${dark ? 'bg-gray-900 text-gray-100' : 'bg-gray-100 text-gray-800'}`}>
            <div className="container mx-auto max-w-4xl">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <Button 
                        icon={<ArrowLeftOutlined />} 
                        onClick={handleBack}
                    >
                        Back to Notifications
                    </Button>
                    <Space>
                        {(content.actionLink || content.primaryAction) && (
                            <Button
                                type="primary"
                                icon={<LinkOutlined />}
                                onClick={handleActionLink}
                            >
                                {content.primaryAction?.label || 'Go to Action'}
                            </Button>
                        )}
                        {!selectedNotification.read && (
                            <Button
                                icon={<EyeOutlined />}
                                onClick={handleMarkAsRead}
                                loading={isMarkingAsRead}
                            >
                                Mark as Read
                            </Button>
                        )}
                        {!selectedNotification.attended && (
                            <Button
                                icon={<CheckOutlined />}
                                onClick={handleMarkAsAttended}
                                loading={isMarkingAsAttended}
                            >
                                Mark as Attended
                            </Button>
                        )}
                        <Button
                            danger
                            icon={<DeleteOutlined />}
                            onClick={handleDelete}
                            loading={isDeleting}
                        >
                            Delete
                        </Button>
                    </Space>
                </div>

                {/* Notification Card */}
                <Card className={dark ? 'bg-gray-800 border-gray-700' : ''}>
                    {/* Status Indicators */}
                    <div className="flex items-center space-x-2 mb-4">
                        {selectedNotification.expired && (
                            <Alert
                                message="This notification has expired"
                                type="warning"
                                icon={<ExclamationCircleOutlined />}
                                className="mb-0"
                            />
                        )}
                        {!selectedNotification.read && (
                            <Tag color="orange">Unread</Tag>
                        )}
                        {selectedNotification.read && (
                            <Tag color="green">Read</Tag>
                        )}
                        {selectedNotification.attended && (
                            <Tag color="blue">Attended</Tag>
                        )}
                        {selectedNotification.attended === false && (
                            <Tag color="default">Not Attended</Tag>
                        )}
                        <Tag color={getCategoryColor(content.category)}>
                            {content.category}
                        </Tag>
                        <Tag color={priorityConfig.color}>
                            {priorityConfig.label}
                        </Tag>
                    </div>

                    {/* Rich Content Display */}
                    <div style={{ marginBottom: 24 }}>
                        {/* Thumbnail */}
                        {content.thumbnail && (
                            <div style={{ marginBottom: 16, textAlign: 'center' }}>
                                <img 
                                    src={content.thumbnail} 
                                    alt="Notification thumbnail"
                                    style={{ 
                                        maxWidth: '100%', 
                                        maxHeight: 200, 
                                        borderRadius: 8,
                                        border: `1px solid ${dark ? '#434343' : '#d9d9d9'}`
                                    }}
                                />
                            </div>
                        )}
                        
                        {/* Action Buttons */}
                        {(content.primaryAction || content.secondaryAction) && (
                            <div style={{ marginBottom: 16 }}>
                                <Space>
                                    {content.primaryAction && (
                                        <Button
                                            type="primary"
                                            size="large"
                                            href={content.primaryAction.url || '#'}
                                            target={content.primaryAction.external ? '_blank' : '_self'}
                                            icon={content.primaryAction.external ? <ArrowRightOutlined /> : <ArrowRightOutlined />}
                                            style={{ marginRight: 8 }}
                                        >
                                            {content.primaryAction.label}
                                        </Button>
                                    )}
                                    {content.secondaryAction && (
                                        <Button
                                            size="large"
                                            href={content.secondaryAction.url || '#'}
                                            target={content.secondaryAction.external ? '_blank' : '_self'}
                                            icon={content.secondaryAction.external ? <ArrowRightOutlined /> : <ArrowRightOutlined />}
                                        >
                                            {content.secondaryAction.label}
                                        </Button>
                                    )}
                                </Space>
                            </div>
                        )}
                    </div>

                    {/* Title */}
                    <Title level={2} className={`mb-4 ${dark ? 'text-white' : ''}`}>
                        {content.title}
                    </Title>

                    {/* Message */}
                    <Paragraph className={`text-lg mb-6 ${dark ? 'text-gray-300' : 'text-gray-700'}`}>
                        {content.message}
                    </Paragraph>

                    <Divider />

                    {/* Details */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <Text strong className={dark ? 'text-white' : ''}>Created:</Text>
                            <br />
                            <Text className={dark ? 'text-gray-300' : ''}>
                                {formatDate(selectedNotification?.createdAt)}
                            </Text>
                            <br />
                            <Text type="secondary">
                                ({formatRelativeDate(selectedNotification?.createdAt)})
                            </Text>
                        </div>

                        {selectedNotification.expiryDate && (
                            <div>
                                <Text strong className={dark ? 'text-white' : ''}>Expires:</Text>
                                <br />
                                <Text className={`${dark ? 'text-gray-300' : ''} ${
                                    selectedNotification.expired ? 'text-red-500' : ''
                                }`}>
                                    {formatDate(selectedNotification.expiryDate)}
                                </Text>
                                <br />
                                <Text type="secondary">
                                    ({formatRelativeDate(selectedNotification.expiryDate)})
                                </Text>
                            </div>
                        )}

                        {selectedNotification.readAt && (
                            <div>
                                <Text strong className={dark ? 'text-white' : ''}>Read:</Text>
                                <br />
                                <Text className={dark ? 'text-gray-300' : ''}>
                                    {formatDate(selectedNotification.readAt)}
                                </Text>
                                <br />
                                <Text type="secondary">
                                    ({formatRelativeDate(selectedNotification.readAt)})
                                </Text>
                            </div>
                        )}

                        {selectedNotification.attendedAt && (
                            <div>
                                <Text strong className={dark ? 'text-white' : ''}>Attended:</Text>
                                <br />
                                <Text className={dark ? 'text-gray-300' : ''}>
                                    {formatDate(selectedNotification.attendedAt)}
                                </Text>
                                <br />
                                <Text type="secondary">
                                    ({formatRelativeDate(selectedNotification.attendedAt)})
                                </Text>
                            </div>
                        )}

                        {selectedNotification.groupCode && (
                            <div>
                                <Text strong className={dark ? 'text-white' : ''}>Group Code:</Text>
                                <br />
                                <Tag>{selectedNotification.groupCode}</Tag>
                            </div>
                        )}

                        {selectedNotification.reference && (
                            <div>
                                <Text strong className={dark ? 'text-white' : ''}>Reference:</Text>
                                <br />
                                <Text className={dark ? 'text-gray-300' : ''}>
                                    {selectedNotification.reference}
                                </Text>
                            </div>
                        )}

                        {selectedNotification.sourceId && (
                            <div>
                                <Text strong className={dark ? 'text-white' : ''}>Source:</Text>
                                <br />
                                <Text className={dark ? 'text-gray-300' : ''}>
                                    {selectedNotification.sourceId}
                                </Text>
                            </div>
                        )}

                        <div>
                            <Text strong className={dark ? 'text-white' : ''}>Language:</Text>
                            <br />
                            <Text className={dark ? 'text-gray-300' : ''}>
                                {selectedNotification.language}
                            </Text>
                        </div>
                    </div>
                </Card>
            </div>
        </div>
    );
};
